/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatErr, fmtDateTime, money, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, StatusPill } from "@/components/shared";
import { toast } from "sonner";
import {
  ChevronLeft,
  Send,
  CalendarClock,
  Car,
  User,
  CreditCard,
  FileSignature,
  FileDown,
  Phone,
  Plus,
  CheckCircle2,
  CircleDot,
  AlertTriangle,
  ShieldAlert,
  MessageCircle,
  Share2,
} from "lucide-react";

const SMS_BUTTONS = [
  { key: "booking_confirmation", label: "Booking", icon: CheckCircle2 },
  { key: "pickup_reminder", label: "Pickup", icon: CalendarClock },
  { key: "return_reminder", label: "Return", icon: CalendarClock },
  { key: "payment_reminder", label: "Payment due", icon: CreditCard },
  { key: "missing_docs", label: "Missing docs", icon: ShieldAlert },
  { key: "overdue", label: "Overdue", icon: AlertTriangle },
  { key: "review_request", label: "Review", icon: CheckCircle2 },
];

const RES_STATUSES = ["Reserved", "Picked Up", "Returned", "Overdue", "Cancelled"];
const METHODS = ["Cash", "Zelle", "Cash App", "Card", "Other"];
const CATEGORIES = [
  { v: "deposit", l: "Deposit" },
  { v: "rental", l: "Rental" },
  { v: "late_fee", l: "Late fee" },
  { v: "toll", l: "Toll" },
  { v: "damage", l: "Damage" },
  { v: "other", l: "Other" },
];

export default function ReservationDetail() {
  const { rid } = useParams();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [res, setRes] = useState(null);
  const [smsLog, setSmsLog] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: 0, method: "Cash", category: "rental", notes: "" });

  const load = async () => {
    const [r, log] = await Promise.all([api.get(`/reservations/${rid}`), api.get("/sms/log")]);
    setRes(r.data);
    setSmsLog(log.data.filter((l) => l.reservation_id === rid));
  };
  useEffect(() => {
    load();
  }, [rid]); // eslint-disable-line

  if (!res) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  const c = res.customer || {};
  const v = res.vehicle || {};
  const payments = res.payments || [];

  const sendSigningLink = async () => {
    try {
      const r = await api.post(`/reservations/${rid}/send-signing-link`);
      const status = r.data?.sms?.status;
      if (status === "sent") toast.success("Signing link sent to customer");
      else if (status === "skipped") toast.info("Salesmsg not configured · link generated but not texted");
      else toast.error(r.data?.sms?.error || "Failed to send link");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const copySigningLink = async () => {
    try {
      const r = await api.post(`/reservations/${rid}/send-signing-link`);
      const link = r.data?.link;
      if (link) {
        const fullLink = link.startsWith("http") ? link : `${window.location.origin}${link}`;
        await navigator.clipboard.writeText(fullLink);
        toast.success("Signing link copied to clipboard");
      }
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const confirmRenewal = async () => {
    const methodInput = window.prompt(
      "Payment method received? (Zelle, Cash App, Cash, Card, Other)",
      res.renewal_requested_method || "Zelle"
    );
    if (!methodInput) return;
    const amountInput = window.prompt(
      "Amount received (in $):",
      String(res.rate_amount || 0)
    );
    if (!amountInput) return;
    const amt = parseFloat(amountInput);
    if (!(amt > 0)) {
      toast.error("Amount must be greater than 0");
      return;
    }
    try {
      const r = await api.post(`/reservations/${rid}/confirm-renewal`, {
        method: methodInput,
        amount: amt,
        notes: "Weekly renewal — confirmed by staff",
      });
      toast.success(`Renewal confirmed — return extended to ${new Date(r.data.new_return_datetime).toLocaleString()}`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const extendRental = async () => {
    const daysStr = window.prompt("Extend rental by how many days? (default 7)", "7");
    if (daysStr === null) return;
    const days = parseInt(daysStr, 10) || 7;
    if (days < 1 || days > 365) {
      toast.error("Days must be 1-365");
      return;
    }
    try {
      const r = await api.post(`/reservations/${rid}/extend`, { days });
      toast.success(`Extended by ${days} day${days !== 1 ? "s" : ""} · new return ${new Date(r.data.return_datetime).toLocaleString()}`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const toggleOpenEnded = async () => {
    const nextVal = !res.open_ended;
    const msg = nextVal
      ? "Make this reservation open-ended? It will never auto-flip to Overdue."
      : "Convert to fixed end date? You'll need to set a return date and overdue tracking will re-activate.";
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/reservations/${rid}/open-ended`, { open_ended: nextVal });
      toast.success(nextVal ? "Set to open-ended" : "Fixed end date — please set a return date");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const openInSms = async () => {
    try {
      const r = await api.post(`/reservations/${rid}/send-signing-link`);
      const link = r.data?.link;
      if (!link) return toast.error("Could not build link");
      const fullLink = link.startsWith("http") ? link : `${window.location.origin}${link}`;
      const phone = (c.phone || "").replace(/[^\d+]/g, "");
      const businessName = "Fair Rideshare LLC";
      const body = `Hi ${c.full_name || "there"}, please review and sign your rental agreement with ${businessName} here: ${fullLink}`;
      // Try the native share sheet first (gives iMessage, WhatsApp, Mail, etc.)
      if (navigator.share) {
        try {
          await navigator.share({ title: "Rental agreement", text: body, url: fullLink });
          return;
        } catch (e) {
          if (e?.name === "AbortError") return; // user cancelled
        }
      }
      // Fallback: sms: URL. Works cross-platform.
      const sep = /iPhone|iPad|iPod|Mac/i.test(navigator.userAgent) ? "&" : "?";
      const smsUrl = `sms:${phone}${phone ? sep : "?"}body=${encodeURIComponent(body)}`;
      window.location.href = smsUrl;
      // If still on page after 1.5s, assume sms: didn't fire — copy to clipboard
      setTimeout(async () => {
        try {
          await navigator.clipboard.writeText(fullLink);
          toast.info("Link copied — paste into iMessage / WhatsApp");
        } catch {
          /* noop */
        }
      }, 1500);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const sendSms = async (key) => {
    try {
      const r = await api.post("/sms/send-template", { reservation_id: rid, template_key: key });
      if (r.data?.status === "sent") toast.success(`${key} sent`);
      else if (r.data?.status === "skipped") toast.info(`Salesmsg not configured · message logged`);
      else toast.error(r.data?.error || "Failed");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const changeStatus = async (status) => {
    try {
      await api.post(`/reservations/${rid}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const addPayment = async () => {
    try {
      if (!payForm.amount) return toast.error("Amount required");
      await api.post("/payments", {
        reservation_id: rid,
        customer_id: res.customer_id,
        amount: Number(payForm.amount),
        method: payForm.method,
        category: payForm.category,
        notes: payForm.notes,
      });
      toast.success("Payment recorded");
      setPayForm({ amount: 0, method: "Cash", category: "rental", notes: "" });
      setPayOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const delPayment = async (pid) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await api.delete(`/payments/${pid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  // Build timeline events (payments + sms)
  const events = [
    ...payments.map((p) => ({ type: "payment", at: p.paid_at, data: p })),
    ...smsLog.map((s) => ({ type: "sms", at: s.sent_at, data: s })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="mb-4">
        <Button variant="ghost" onClick={() => nav("/admin/reservations")} data-testid="back-button">
          <ChevronLeft size={16} className="mr-1" /> All reservations
        </Button>
      </div>

      <PageHeader
        title={`${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim() || "Reservation"}
        subtitle={`Reservation · ${res.id?.slice(0, 8)}`}
        testId="reservation-detail-header"
        action={
          <div className="flex items-center gap-2">
            <StatusPill status={res.status} />
            <Select value={res.status} onValueChange={changeStatus}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="detail-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RES_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {res.renewal_pending && (
        <div
          data-testid="renewal-pending-banner"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <div className="text-sm text-amber-900">
            <div className="font-semibold flex items-center gap-1.5">
              <ShieldAlert size={14} /> Renewal request pending
            </div>
            <div className="text-xs text-amber-800 mt-0.5">
              Customer submitted a renewal request
              {res.renewal_requested_method ? ` via ${res.renewal_requested_method}` : ""}
              {res.renewal_requested_at ? ` on ${new Date(res.renewal_requested_at).toLocaleString()}` : ""}.
              {res.renewal_requested_note && (
                <span className="block italic mt-0.5">"{res.renewal_requested_note}"</span>
              )}
            </div>
          </div>
          <Button
            size="sm"
            onClick={confirmRenewal}
            data-testid="confirm-renewal-btn"
            className="bg-amber-900 hover:bg-amber-950 text-white shrink-0"
          >
            <CheckCircle2 size={13} className="mr-1.5" />
            Confirm payment · extend +7 days
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: customer + vehicle */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="overline mb-2 flex items-center gap-1.5"><User size={11} /> Customer</div>
            <div className="font-heading text-lg font-semibold">{c.full_name}</div>
            {c.phone && (
              <div className="text-sm text-gray-700 mt-1 flex items-center gap-2">
                <Phone size={12} className="text-gray-400" /> {c.phone}
              </div>
            )}
            {c.email && <div className="text-sm text-gray-600">{c.email}</div>}
            {c.address && <div className="text-xs text-gray-500 mt-1">{c.address}</div>}
            {c.license_number && (
              <div className="text-xs text-gray-500 mt-2">
                License: <span className="font-mono">{c.license_number}</span> · exp {fmtDate(c.license_expiration)}
              </div>
            )}
            {c.high_risk && (
              <div className="mt-2 pill pill-red"><ShieldAlert size={11} /> High risk</div>
            )}
            <Button size="sm" variant="outline" className="mt-3" onClick={() => nav(`/admin/customers`)} data-testid="open-customer">
              View profile
            </Button>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="overline mb-2 flex items-center gap-1.5"><Car size={11} /> Vehicle</div>
            <div className="font-heading text-lg font-semibold">{v.year} {v.make} {v.model}</div>
            <div className="text-sm text-gray-700 mt-1 font-mono">{v.plate || "—"}</div>
            <div className="text-xs text-gray-500 mt-1">VIN <span className="font-mono">{v.vin || "—"}</span></div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center text-xs">
              <div><div className="overline">Daily</div><div className="tabular-nums font-semibold">{money(v.daily_rate)}</div></div>
              <div><div className="overline">Weekly</div><div className="tabular-nums font-semibold">{money(v.weekly_rate)}</div></div>
              <div><div className="overline">Deposit</div><div className="tabular-nums font-semibold">{money(v.deposit_amount)}</div></div>
            </div>
          </div>
        </div>

        {/* Middle: dates + totals + SMS actions */}
        <div className="space-y-4 lg:col-span-2">
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="overline">Pickup</div>
                <div className="text-sm font-medium mt-1">{fmtDateTime(res.pickup_datetime)}</div>
              </div>
              <div>
                <div className="overline">Return</div>
                <div className="text-sm font-medium mt-1">
                  {res.open_ended ? (
                    <span className="inline-flex items-center gap-1 text-blue-700" data-testid="open-ended-badge">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                      Open-ended
                    </span>
                  ) : (
                    fmtDateTime(res.return_datetime)
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-2">
                  <button
                    onClick={toggleOpenEnded}
                    className="text-[10px] uppercase tracking-wide text-gray-500 hover:text-gray-900 underline underline-offset-2"
                    data-testid="toggle-open-ended"
                  >
                    {res.open_ended ? "Set fixed end date" : "Make open-ended"}
                  </button>
                  <button
                    onClick={extendRental}
                    className="text-[10px] uppercase tracking-wide text-gray-500 hover:text-gray-900 underline underline-offset-2"
                    data-testid="extend-rental"
                  >
                    Extend rental
                  </button>
                  {res.signing_token && (
                    <a
                      href={`/renew/${res.signing_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] uppercase tracking-wide text-gray-500 hover:text-gray-900 underline underline-offset-2"
                      data-testid="renew-rental-staff"
                    >
                      Renew rental
                    </a>
                  )}
                </div>
              </div>
              <div>
                <div className="overline">Rate</div>
                <div className="text-sm font-medium mt-1 capitalize">{res.rate_type} · {money(res.rate_amount)}</div>
              </div>
              <div>
                <div className="overline">Days</div>
                <div className="text-sm font-medium mt-1 tabular-nums">{res.rental_days || "—"}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-gray-100">
              <div>
                <div className="overline">Paid</div>
                <div className="font-heading text-2xl font-bold mt-1 tabular-nums text-green-700">{money(res.amount_paid)}</div>
              </div>
              <div>
                <div className="overline">Weekly Outstanding</div>
                <div className={`font-heading text-2xl font-bold mt-1 tabular-nums ${res.balance_due > 0 ? "text-amber-700" : "text-gray-400"}`}>{money(res.balance_due)}</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline">Quick Actions</div>
                <h3 className="font-heading text-lg font-semibold">Send SMS</h3>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {SMS_BUTTONS.map((b) => (
                <Button
                  key={b.key}
                  variant="outline"
                  size="sm"
                  onClick={() => sendSms(b.key)}
                  data-testid={`send-sms-${b.key}`}
                  className="border-gray-200 hover:border-gray-900 hover:bg-gray-50"
                >
                  <b.icon size={13} className="mr-1.5" />
                  {b.label}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => nav(`/admin/agreements/${rid}`)}
                data-testid="open-agreement"
                className="border-gray-200 hover:border-gray-900 hover:bg-gray-50"
              >
                <FileSignature size={13} className="mr-1.5" />
                Agreement
              </Button>
              {res.signing_token && res.agreement?.customer_signature && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/agreement/${res.signing_token}/pdf`, "_blank", "noopener")}
                  data-testid="download-signed-pdf"
                  className="border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900"
                >
                  <FileDown size={13} className="mr-1.5" />
                  Signed PDF
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={openInSms}
                data-testid="open-in-sms"
                className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
              >
                <MessageCircle size={13} className="mr-1.5" />
                Text from my phone
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={sendSigningLink}
                data-testid="send-signing-link"
                className="border-gray-200 hover:border-gray-900 hover:bg-gray-50"
              >
                <Send size={13} className="mr-1.5" />
                Text sign-link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copySigningLink}
                data-testid="copy-signing-link"
                className="border-gray-200 hover:border-gray-900 hover:bg-gray-50"
              >
                <Share2 size={13} className="mr-1.5" />
                Copy sign-link
              </Button>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline">Activity</div>
                <h3 className="font-heading text-lg font-semibold">Timeline</h3>
              </div>
              <Button size="sm" onClick={() => setPayOpen(true)} className="bg-black hover:bg-gray-800 text-white" data-testid="add-payment-detail">
                <Plus size={13} className="mr-1.5" /> Record payment
              </Button>
            </div>
            {events.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No activity yet.</p>
            ) : (
              <ol className="relative border-l border-gray-200 ml-3" data-testid="reservation-timeline">
                {events.map((e, idx) => (
                  <li key={idx} className="ml-5 pb-5 last:pb-0">
                    <span className={`absolute -left-1.5 w-3 h-3 rounded-full ${e.type === "payment" ? "bg-green-500" : "bg-blue-500"}`} />
                    <div className="text-xs text-gray-500">{fmtDateTime(e.at)}</div>
                    {e.type === "payment" ? (
                      <div className="mt-1 flex items-center gap-2">
                        <CircleDot size={13} className="text-green-600" />
                        <div className="flex-1">
                          <span className="font-medium">{money(e.data.amount)}</span>{" "}
                          <span className="text-gray-600 text-sm">
                            via {e.data.method} · <span className="capitalize">{e.data.category?.replace("_", " ")}</span>
                          </span>
                          {e.data.notes && <div className="text-xs text-gray-500">{e.data.notes}</div>}
                        </div>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 h-7 w-7 p-0" onClick={() => delPayment(e.data.id)} data-testid={`delete-payment-${e.data.id}`}>
                            ×
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="mt-1 flex items-start gap-2">
                        <Send size={13} className="text-blue-600 mt-0.5" />
                        <div className="text-sm text-gray-700">
                          <span className="font-medium capitalize">{e.data.template_key?.replace(/_/g, " ") || "SMS"}</span>{" "}
                          <span className={`text-xs ${e.data.status === "sent" ? "text-green-700" : e.data.status === "failed" ? "text-red-700" : "text-amber-700"}`}>
                            · {e.data.status}
                          </span>
                          <div className="text-xs text-gray-500 line-clamp-2">{e.data.body}</div>
                          {e.data.error && <div className="text-xs text-red-600">{e.data.error}</div>}
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-lg" data-testid="payment-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount</Label>
                <Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="detail-pay-amount" />
              </div>
              <div>
                <Label>Method</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger data-testid="detail-pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={payForm.category} onValueChange={(v) => setPayForm({ ...payForm, category: v })}>
                <SelectTrigger data-testid="detail-pay-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} data-testid="detail-pay-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={addPayment} className="bg-black hover:bg-gray-800 text-white" data-testid="detail-pay-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
